import { Box, Calendar, ExternalLink, Package, PackageX, ShoppingCart, Store, Weight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { DynamicDeliveryTag } from '@/components/DynamicDeliveryTag';
import { FreefoodIcon } from '@/components/FreefoodIcon';
import { EllipsisTooltip } from '@/components/EllipsisTooltip';

import { colors } from 'constants/colors';
import { formatVolumeInLiters, formatWeight } from 'utils/units';

import { formatDateAsAnniversary } from '../../../helpers/dateTimes';
import { GetDeliveriesQuery } from 'data/__generated__';

function getDeliveryPersonName(
    deliveryPerson: GetDeliveriesQuery['deliveries'][number]['deliveryPerson'],
): string | null {
    if (!deliveryPerson) return null;
    const { profile, username } = deliveryPerson;
    const fromNames = [profile.firstname, profile.lastname].filter(Boolean).join(' ');
    return profile.fullname || fromNames || username || null;
}

const DESTINATION_PREFIX_REGEX = /^(STORE|FREEFOOD|PALETTE)\s+/;

// The label embeds the destination as a prefix (e.g. "STORE Balas", "FREEFOOD AXA BIC #62 …").
// We surface that via logos instead, so strip the keyword from each " + "-joined segment.
function cleanDeliveryTitle(label: string): string {
    return label
        .split(' + ')
        .map((segment) => segment.replace(DESTINATION_PREFIX_REGEX, ''))
        .join(' + ');
}

export const DeliveryTrelloItem = ({
    delivery,
    isSelected,
    toggleDeliverySelection,
}: {
    delivery: GetDeliveriesQuery['deliveries'][number];
    isSelected: boolean;
    toggleDeliverySelection: (deliveryId: string) => void;
}) => {
    const {
        _id: deliveryId,
        contentSummary,
        deliveryDate,
        deliveryPerson,
        isDynamic,
        label,
        orders,
        roundColor,
        roundName,
        siteId,
        soldoutRefsCount,
    } = delivery;
    const { boxCount, productCount, totalVolume, totalWeight } = contentSummary;

    const deliveryPersonName = getDeliveryPersonName(deliveryPerson);
    const headerColor = roundColor || colors.pantinGrey;
    const title = cleanDeliveryTitle(label);
    const hasStore = siteId != null;
    const hasFreefood = orders.length > 0;

    return (
        <div
            className={cn(
                'overflow-hidden rounded-[4px] border border-border bg-filter shadow-xs transition-shadow hover:shadow-sm',
                isSelected && 'ring-2 ring-primary',
            )}
        >
            <div className="flex items-center gap-2 px-2.5 py-1.5 text-white" style={{ backgroundColor: headerColor }}>
                <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleDeliverySelection(deliveryId)}
                    aria-label="Sélectionner la livraison"
                    className="shrink-0 border-white/70 bg-white/10 data-checked:border-white data-checked:bg-white data-checked:text-black"
                />
                <span className="min-w-0 truncate text-sm font-semibold">
                    {roundName || ''}
                    {deliveryPersonName ? <span className="text-xs"> · {deliveryPersonName}</span> : null}
                </span>
                <Link
                    to={`/delivery/${deliveryId}`}
                    className="ml-auto inline-flex size-6 shrink-0 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/20 hover:text-white"
                    aria-label="Ouvrir la livraison"
                >
                    <ExternalLink className="size-3.5" />
                </Link>
            </div>

            <div className="flex flex-col gap-1.5 px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                    <EllipsisTooltip
                        text={title}
                        className="min-w-0 text-xl font-extrabold leading-tight text-foreground"
                    />
                    <DynamicDeliveryTag isDynamic={isDynamic} label="Livraison dynamique" />
                </div>

                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="size-3.5 shrink-0" />
                    {formatDateAsAnniversary({ dateTime: deliveryDate, displayDay: true })}
                </div>

                {boxCount > 0 || soldoutRefsCount > 0 || hasStore || hasFreefood ? (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        {boxCount > 0 ? (
                            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
                                <span className="inline-flex items-center gap-1" title="Nombre de box">
                                    <Package className="size-3.5 shrink-0" />
                                    {boxCount}
                                </span>
                                <span className="inline-flex items-center gap-1" title="Nombre de produits">
                                    <ShoppingCart className="size-3.5 shrink-0" />
                                    {productCount}
                                </span>
                                <span className="inline-flex items-center gap-1" title="Poids total">
                                    <Weight className="size-3.5 shrink-0" />
                                    {formatWeight(totalWeight)}
                                </span>
                                <span className="inline-flex items-center gap-1" title="Volume total">
                                    <Box className="size-3.5 shrink-0" />
                                    {formatVolumeInLiters(totalVolume)}
                                </span>
                            </div>
                        ) : null}
                        {soldoutRefsCount > 0 || hasStore || hasFreefood ? (
                            <div className="ml-auto flex shrink-0 items-center gap-2">
                                {soldoutRefsCount > 0 ? (
                                    <span
                                        className="inline-flex items-center gap-1 font-semibold text-destructive"
                                        title="Références en rupture (soldout online + bundle)"
                                    >
                                        <PackageX className="size-3.5 shrink-0" />
                                        {soldoutRefsCount}
                                    </span>
                                ) : null}
                                {hasStore || hasFreefood ? (
                                    <span className="flex items-center gap-1.5 text-foreground">
                                        {hasStore ? (
                                            <span className="inline-flex" title="Store">
                                                <Store className="size-4 shrink-0" />
                                            </span>
                                        ) : null}
                                        {hasFreefood ? <FreefoodIcon /> : null}
                                    </span>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
